import { supabase } from '@/integrations/supabase/client';
import { CompanyUserService } from '@/services/CompanyUserService';

/**
 * Checks if the current user is an admin
 * @param userId - The user ID to check
 * @returns Promise<boolean>
 */
export const isUserAdmin = async (userId: string): Promise<boolean> => {
  try {
    if (!userId) return false;
    
    // Check if user has admin profile
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('user_id', userId)
      .single();
      
    if (error || !profile) return false;
    
    return profile.user_type === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Checks if the current user is an employee (company admin or member)
 * or a platform admin
 */
export const isUserAdminOrEmployee = async (): Promise<boolean> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    // First check if user is an admin
    const isAdmin = await isUserAdmin(user.id);
    if (isAdmin) return true;
    
    // If not admin, check if user is an employee
    return await CompanyUserService.isUserEmployee(user.id);
  } catch (error) {
    console.error('Error checking admin or employee status:', error);
    return false;
  }
};

/**
 * Direct check if a user has admin privileges by user_id
 * This can be used for synchronous checks or when user_id is already known
 */
export const checkUserAdminById = async (userId: string): Promise<boolean> => {
  try {
    if (!userId) return false;
    
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('user_id', userId)
      .single();
      
    if (error || !profile) return false;
    
    return profile.user_type === 'admin';
  } catch (error) {
    console.error('Error checking admin status by ID:', error);
    return false;
  }
};

/**
 * Gets the current user's ID
 */
export const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.error('Error getting current user ID:', error);
    return null;
  }
};

/**
 * Gets the user's role from their profile
 * @param userId - The user ID to check
 * @returns Promise<string | null>
 */
export const getUserRole = async (userId: string): Promise<string | null> => {
  try {
    if (!userId) return null;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('user_id', userId)
      .single();

    if (error || !profile) return null;

    return profile.user_type;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};
